import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus, WarningType } from 'api/account';
import { ErrorCodes } from 'api/errors/types';
import { PermissionSetType } from 'api/models';
import { Alert } from 'components/atoms/alert';
import { Caption01 } from 'components/styles';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAccount } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { useAccountDataQuery } from 'queries/account';
import { ContactSupportModal } from './contact-support-modal';
const AnnouncementBanner = styled(Alert)`
  padding: var(--spacing-03) var(--spacing-06);
  .ant-alert-message {
    width: auto;
    text-align: center;
    ${Caption01};
  }
`;
const Container = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--zindex-modal);
  display: flex;
  justify-content: center;
  max-width: calc(100vw - 32px);

  ${AnnouncementBanner} {
    width: max-content;
    max-width: 100%;
  }
`;
const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-02);
`;
const CtaButton = styled.span`
  white-space: nowrap;
  text-decoration: underline;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
const ARR_THRESHOLD = 50000;
const BANNER_CONFIG = {
    nitro_out_of_credits: {
        alertType: 'error',
        messageKey: 'nitro.banner.out_of_credits',
        showContactCta: true
    },
    nitro_out_of_credits_payment_due: {
        alertType: 'error',
        messageKey: 'nitro.banner.out_of_credits_payment_due',
        showContactCta: true
    },
    nitro_credits_expired: {
        alertType: 'error',
        messageKey: 'nitro.banner.credits_expired',
        showContactCta: true
    },
    nitro_credits_expired_payment_due: {
        alertType: 'error',
        messageKey: 'nitro.banner.credits_expired_payment_due',
        showContactCta: true
    },
    payment_due_nitro: {
        alertType: 'error',
        messageKey: 'nitro.banner.payment_due_nitro',
        showContactCta: true
    },
    payment_due_non_nitro: {
        alertType: 'error',
        messageKey: 'billing.banner.payment_due_non_nitro',
        showContactCta: false
    },
    payment_due_nitro_100: {
        alertType: 'error',
        messageKey: 'nitro.banner.payment_due_100_percent',
        showContactCta: true
    },
    payment_due_nitro_90: {
        alertType: 'error',
        messageKey: 'nitro.banner.payment_due_90_percent',
        showContactCta: true
    },
    payment_due_nitro_80: {
        alertType: 'error',
        messageKey: 'nitro.banner.payment_due_80_percent',
        showContactCta: true
    },
    nitro_100_percent: {
        alertType: 'error',
        messageKey: 'nitro.banner.100_percent_used',
        showContactCta: true
    },
    nitro_90_percent: {
        alertType: 'warning',
        messageKey: 'nitro.banner.90_percent_used',
        showContactCta: true
    },
    nitro_80_percent: {
        alertType: 'info',
        messageKey: 'nitro.banner.80_percent_used',
        showContactCta: true
    }
};
export const AccountBannerV2 = ()=>{
    const { accountWarnings, status } = useAccount();
    const { data: accountData } = useAccountDataQuery();
    const currentUserPermissionSetType = useUserPermissionSetType();
    const { hasAccessToResource, isLoading: isRbacLoading } = useWithAccess();
    const translate = useTranslate();
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const isNitroAccount = useMemo(()=>{
        var _accountData_accountMeta_features, _accountData_accountMeta;
        return Boolean(accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.scarlet);
    }, [
        accountData
    ]);
    const isAccountAdmin = currentUserPermissionSetType === PermissionSetType.AccountAdmin;
    const hasBillingRead = useMemo(()=>hasAccessToResource({
            rule: 'BILLING:READ'
        }), [
        hasAccessToResource
    ]);
    const hasNitroCreditRead = useMemo(()=>hasAccessToResource({
            rule: 'NITRO_CREDIT:READ'
        }), [
        hasAccessToResource
    ]);
    const isHighARR = useMemo(()=>{
        var _accountData_accountMeta;
        const arr = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : _accountData_accountMeta.arr;
        return typeof arr === 'number' && arr >= ARR_THRESHOLD;
    }, [
        accountData
    ]);
    // High-ARR accounts: only admins or users with BILLING:READ / NITRO_CREDIT:READ.
    // Low/null ARR: banners are visible to everyone. Same gate for payment + Nitro.
    const canSeeBanners = useMemo(()=>!isHighARR || isAccountAdmin || hasBillingRead || hasNitroCreditRead, [
        isHighARR,
        isAccountAdmin,
        hasBillingRead,
        hasNitroCreditRead
    ]);
    const warnings = useMemo(()=>{
        const has = (type, code)=>{
            var _ref;
            return (_ref = accountWarnings === null || accountWarnings === void 0 ? void 0 : accountWarnings.some((w)=>w.warningType === type && w.errorCode === code)) !== null && _ref !== void 0 ? _ref : false;
        };
        return {
            paymentFailed: has(WarningType.Billing, ErrorCodes.PaymentFailed),
            nitroOutOfCredits: has(WarningType.Nitro, ErrorCodes.NitroOutOfCredits),
            nitroCreditsExpired: has(WarningType.Nitro, ErrorCodes.NitroCreditsExpired),
            nitro100Percent: has(WarningType.Nitro, ErrorCodes.Nitro100PercentUsed),
            nitro90Percent: has(WarningType.Nitro, ErrorCodes.Nitro90PercentUsed),
            nitro80Percent: has(WarningType.Nitro, ErrorCodes.Nitro80PercentUsed)
        };
    }, [
        accountWarnings
    ]);
    const bannerVariant = useMemo(()=>{
        const { paymentFailed, nitroOutOfCredits, nitroCreditsExpired, nitro100Percent, nitro90Percent, nitro80Percent } = warnings;
        // Priority 90 — agents stopped: out of credits
        if (isNitroAccount && nitroOutOfCredits && canSeeBanners) {
            if (paymentFailed) {
                return 'nitro_out_of_credits_payment_due';
            }
            return 'nitro_out_of_credits';
        }
        // Priority 88 — agents stopped: credits expired by date
        if (isNitroAccount && nitroCreditsExpired && canSeeBanners) {
            if (paymentFailed) {
                return 'nitro_credits_expired_payment_due';
            }
            return 'nitro_credits_expired';
        }
        // Priority 70 — payment due (combined with Nitro % used for Nitro accounts)
        if (paymentFailed && canSeeBanners) {
            if (isNitroAccount) {
                if (nitro100Percent) {
                    return 'payment_due_nitro_100';
                }
                if (nitro90Percent) {
                    return 'payment_due_nitro_90';
                }
                if (nitro80Percent) {
                    return 'payment_due_nitro_80';
                }
                return 'payment_due_nitro';
            }
            return 'payment_due_non_nitro';
        }
        // Priority 65 — 100% used, grace period
        if (isNitroAccount && nitro100Percent && canSeeBanners) {
            return 'nitro_100_percent';
        }
        // Priority 40 — 90% used
        if (isNitroAccount && nitro90Percent && canSeeBanners) {
            return 'nitro_90_percent';
        }
        // Priority 20 — 80% used
        if (isNitroAccount && nitro80Percent && canSeeBanners) {
            return 'nitro_80_percent';
        }
        return null;
    }, [
        warnings,
        isNitroAccount,
        canSeeBanners
    ]);
    if (isRbacLoading) {
        return null;
    }
    if (status === AccountStatus.Trial) {
        return null;
    }
    if (!bannerVariant) {
        return null;
    }
    const config = BANNER_CONFIG[bannerVariant];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Container, {
                id: "account-banner-v2",
                children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
                    type: config.alertType,
                    closable: false,
                    message: /*#__PURE__*/ _jsxs(BannerContent, {
                        "data-cy": "account-banner-v2.message",
                        children: [
                            /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: config.messageKey
                            }),
                            config.showContactCta && /*#__PURE__*/ _jsx(CtaButton, {
                                role: "button",
                                tabIndex: 0,
                                onClick: ()=>setIsContactModalOpen(true),
                                onKeyDown: (e)=>{
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setIsContactModalOpen(true);
                                    }
                                },
                                "data-cy": "account-banner-v2.contact-cta",
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "nitro.banner.contact_cta"
                                })
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(ContactSupportModal, {
                visible: isContactModalOpen,
                bannerMessage: translate(config.messageKey),
                onClose: ()=>setIsContactModalOpen(false)
            })
        ]
    });
};
